
import type { Vitals } from "../net/connection";

/** An entity with no `max_hp` yet has no bar. Drawing an empty frame over every
 *  NPC the moment it appears reads as "everything is dead". */
export function hasHealth(vitals: Vitals): boolean {
  return vitals.maxHp > 0;
}

export function healthFraction(hp: number, maxHp: number): number {
  if (maxHp <= 0) return 0;
  const fraction = hp / maxHp;
  return fraction <= 0 ? 0 : fraction >= 1 ? 1 : fraction;
}

export interface BarRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export function barGeometry(
  centreXPx: number,
  anchorYPx: number,
  widthPx: number,
  heightPx: number,
  liftPx: number,
): BarRect {
  return {
    x: Math.round(centreXPx - widthPx / 2),
    y: Math.round(anchorYPx - liftPx - heightPx),
    width: Math.round(widthPx),
    height: Math.round(heightPx),
  };
}

/** The lit part, filled from the left edge. Never wider than the frame. */
export function barFillWidth(rect: BarRect, fraction: number): number {
  const clamped = fraction <= 0 ? 0 : fraction >= 1 ? 1 : fraction;
  return Math.round(rect.width * clamped);
}

export function healthColour(fraction: number): string {
  if (fraction <= 0.25) return "#d9534f";
  if (fraction <= 0.55) return "#e2a03f";
  return "#5cb85c";
}

export const LEVEL_COLOURS = {
  trivial: "#8d9aa8",
  easy: "#79c17a",
  even: "#e9eef5",
  hard: "#e2a03f",
  deadly: "#e0605c",
} as const;

export type LevelTier = keyof typeof LEVEL_COLOURS;

export function levelTier(theirLevel: number, myLevel: number): LevelTier {
  const delta = theirLevel - myLevel;
  if (delta <= -5) return "trivial";
  if (delta < 0) return "easy";
  if (delta === 0) return "even";
  if (delta <= 4) return "hard";
  return "deadly";
}

export function levelColour(theirLevel: number, myLevel: number): string {
  return LEVEL_COLOURS[levelTier(theirLevel, myLevel)];
}

export function levelTail(level: number): string {
  return level > 0 ? `Lv ${level}` : "";
}

export const LEVEL_TAIL_COLOUR = "#98ff33";
